/**
 * Role-based utility functions for checking user permissions
 * 
 * SECURITY NOTE: These functions use Vuex store which is populated from API.
 * Do NOT use localStorage for role checks as it can be manipulated by users.
 */

import store from '@/store';

/**
 * Get the current user's role from Vuex store (secure)
 * @returns {string} The user's role or empty string if not found
 */
export function getCurrentUserRole() {
  return store.getters['auth/userRole'] || "";
}

/**
 * Check if the current user has any of the specified roles
 * @param {string|string[]} allowedRoles - Single role or array of roles
 * @returns {boolean} True if user has one of the allowed roles
 */
export function hasRole(allowedRoles) {
  const currentRole = getCurrentUserRole();
  
  if (Array.isArray(allowedRoles)) {
    return allowedRoles.includes(currentRole);
  }
  
  return currentRole === allowedRoles;
}

/**
 * Check if user can access transfer functionality
 * @returns {boolean} True if user has finance or admin role
 */
export function canAccessTransfer() {
  return store.getters['auth/canAccessTransfer'] || false;
}

/**
 * Check if user can add balance
 * @returns {boolean} True if user has admin or finance role
 */
export function canAddBalance() {
  return hasRole(["Admin", "Finance"]);
}

/**
 * Check if user is admin
 * @returns {boolean} True if user has admin role
 */
export function isAdmin() {
  return store.getters['auth/isAdmin'] || false;
}

/**
 * Check if user is finance
 * @returns {boolean} True if user has finance role
 */
export function isFinance() {
  return store.getters['auth/isFinance'] || false;
}

/**
 * Check if user is staff
 * @returns {boolean} True if user has staff role
 */
export function isStaff() {
  return store.getters['auth/isStaff'] || false;
}

/**
 * Check if user is IT
 * @returns {boolean} True if user has IT role
 */
export function isIT() {
  return store.getters['auth/isIT'] || false;
}

/**
 * Get KYB status from store (secure)
 * @returns {string} The KYB status or null if not found
 */
export function getStatusKyb() {
  return store.getters['auth/statusKyb'] || null;
}

/**
 * Get merchant ID from store or localStorage
 * @returns {number|null} The merchant ID or null if not found
 */
export function getMerchantId() {
  const merchantId = store.getters['auth/merchantId'];
  if (merchantId) {
    return merchantId;
  }
  // Fallback to localStorage if store doesn't have it yet
  const storedId = localStorage.getItem('merchantId');
  return storedId ? parseInt(storedId, 10) : null;
}

/**
 * Get company name from store or localStorage
 * @returns {string|null} The company name or null if not found
 */
export function getCompanyName() {
  const companyName = store.getters['auth/companyName'];
  if (companyName) {
    return companyName;
  }
  // Fallback to localStorage if store doesn't have it yet
  return localStorage.getItem('merchantName') || null;
}

/**
 * Check if current account is a demo account
 * @returns {boolean} True if is_account_demo === true from API profile
 */
export function isAkunDemo() {
  return store.getters['auth/isAccountDemo'] || false;
}

/**
 * Check if KYB status is approved
 * @returns {boolean} True if statusKyb === 'approved'
 */
export function isKybApproved() {
  return store.getters['auth/isKybApproved'] || false;
}
